/**
 * FASE 21.1 — MUTATION TOOL CONTRACTS
 *
 * Typed write-action definitions. Every mutation Kevia can propose
 * must be declared here. No undeclared writes are possible.
 *
 * INVARIANTS:
 *  - No raw SQL. No AI-generated payloads. No unknown fields.
 *  - All mutations require human confirmation before execution.
 *  - confirmationRequired is always true — enforced by executeMutation.
 */

/** @typedef {'pastoral_action'|'pastoral_care'|'administrative'|'member_update'|'pastoral_planning'|'status_change'} AuditType */

/**
 * @typedef {Object} MutationSchema
 * @property {string}    id
 * @property {string}    label              — shown in confirm card
 * @property {string}    description        — pastoral explanation
 * @property {string}    entityType         — "visit"|"prayer"|"event"|"note"|"person"
 * @property {string[]}  allowedRoles       — ["*"] = any authenticated role
 * @property {string[]}  requiredFields     — payload fields that must be present
 * @property {string[]}  optionalFields     — payload fields that may be present
 * @property {true}      confirmationRequired — always true (safety invariant)
 * @property {number}    cooldownMs         — min ms between identical mutations
 * @property {'queue'|'reject'} offlineBehavior — queue: add to offline outbox; reject: block when offline
 * @property {AuditType} auditType
 */

export const MUTATION_TOOLS = Object.freeze({

  create_visit: {
    id:                  "create_visit",
    label:               "Registrar visita",
    description:         "Registrar una visita pastoral a una familia o persona",
    entityType:          "visit",
    allowedRoles:        ["*"],
    requiredFields:      ["contact_name", "visit_type", "date"],
    optionalFields:      ["time", "target_family_id", "target_person_id", "district_id", "church_id"],
    confirmationRequired: true,
    cooldownMs:          5_000,
    offlineBehavior:     "queue",
    auditType:           "pastoral_action",
  },

  create_prayer: {
    id:                  "create_prayer",
    label:               "Registrar oración",
    description:         "Registrar una solicitud de oración pastoral",
    entityType:          "prayer",
    allowedRoles:        ["*"],
    requiredFields:      ["contact_name", "prayer_type"],
    optionalFields:      ["notes", "target_person_id", "target_family_id", "district_id", "church_id"],
    confirmationRequired: true,
    cooldownMs:          3_000,
    offlineBehavior:     "queue",
    auditType:           "pastoral_care",
  },

  create_event: {
    id:                  "create_event",
    label:               "Crear evento",
    description:         "Crear un evento pastoral o de iglesia",
    entityType:          "event",
    allowedRoles:        ["coordinator", "district_pastor", "admin"],
    requiredFields:      ["title", "date", "event_type"],
    optionalFields:      ["time", "location_name", "description", "church_id", "district_id"],
    confirmationRequired: true,
    cooldownMs:          5_000,
    offlineBehavior:     "queue",
    auditType:           "administrative",
  },

  create_family_note: {
    id:                  "create_family_note",
    label:               "Agregar nota",
    description:         "Agregar una nota pastoral a una familia o entidad",
    entityType:          "note",
    allowedRoles:        ["*"],
    requiredFields:      ["content", "entity_id", "entity_type"],
    optionalFields:      ["visibility_type", "district_id"],
    confirmationRequired: true,
    cooldownMs:          2_000,
    offlineBehavior:     "reject",
    auditType:           "pastoral_care",
  },

  schedule_followup: {
    id:                  "schedule_followup",
    label:               "Programar seguimiento",
    description:         "Programar una visita de seguimiento pastoral",
    entityType:          "visit",
    allowedRoles:        ["*"],
    requiredFields:      ["contact_name", "followup_date"],
    optionalFields:      ["visit_type", "target_family_id", "target_person_id", "district_id", "church_id"],
    confirmationRequired: true,
    cooldownMs:          3_000,
    offlineBehavior:     "queue",
    auditType:           "pastoral_planning",
  },

  update_visit_status: {
    id:                  "update_visit_status",
    label:               "Actualizar visita",
    description:         "Actualizar el estado de una visita pastoral",
    entityType:          "visit",
    allowedRoles:        ["*"],
    requiredFields:      ["visit_id", "new_status"],
    optionalFields:      ["district_id"],
    confirmationRequired: true,
    cooldownMs:          2_000,
    offlineBehavior:     "queue",
    auditType:           "status_change",
  },

  update_person_status: {
    id:                  "update_person_status",
    label:               "Actualizar estado de persona",
    description:         "Actualizar el estado espiritual o de bautismo de una persona",
    entityType:          "person",
    allowedRoles:        ["coordinator", "district_pastor", "admin"],
    requiredFields:      ["person_id", "status_field", "new_value"],
    optionalFields:      ["family_id"],
    confirmationRequired: true,
    cooldownMs:          5_000,
    offlineBehavior:     "queue",
    auditType:           "member_update",
  },

});

export const ALLOWED_PERSON_STATUS_FIELDS = Object.freeze([
  "baptism_status", "spiritual_status",
]);

export const ALLOWED_VISIT_STATUSES = Object.freeze([
  "pendiente", "realizada", "rescheduled",
]);

export const ALLOWED_VISIT_TYPES = Object.freeze([
  "pastoral", "seguimiento", "bienestar", "evangelismo", "disciplinado",
]);

export const ALLOWED_PRAYER_TYPES = Object.freeze([
  "sanidad", "familia", "trabajo", "espiritual", "peticion_general", "intercesion",
]);

export function getMutationSchemaById(id) {
  return MUTATION_TOOLS[id] ?? null;
}

export function getAllMutationSchemas() {
  return Object.values(MUTATION_TOOLS);
}
